import React from 'react';
import { Box, Button, IconButton, FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import { VolumeUp as SpeakerIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import UnitPageShell from '../../components/shared/UnitPageShell';
import ZoomableImage from '../../components/shared/ZoomableImage';
import EditableTextBlock from '../../components/shared/EditableTextBlock';
import { useAuth } from '../../context/AuthContext';
import { useTTS } from '../../hooks/useTTS';
import { uploadUnitAsset, updateUnitContent, useUnitOverride } from '../../services/unitOverridesService';

const PAGE_ID = 'm6_u7_x1_sicherung';
const IMAGE_BASE = '/assets/drawable/';
const IMAGE_KEYS = ['img_0'];
const IMAGE_KEYS_DYNAMIC = ['img_0'];

interface SicherungInfo {
  name: string;
  titel: string;
  allgemeineBeschreibung: string;
  auslegungsHinweise: string;
  details: string;
}

const sicherungstypen: SicherungInfo[] = [
  {
    name: 'LS-Automat (B-Charakteristik)',
    titel: 'Leitungsschutzschalter (LS) - B-Charakteristik',
    allgemeineBeschreibung: 'Leitungsschutzschalter, auch Sicherungsautomaten genannt, schützen elektrische Leitungen und angeschlossene Geräte vor Überlast und Kurzschluss. Sie sind wiederverwendbar und müssen nach dem Auslösen nur wieder eingeschaltet werden.',
    auslegungsHinweise: 'Die Auslegung eines LS-Automaten hängt vom Querschnitt der zu schützenden Leitung und der Art der angeschlossenen Verbraucher ab. Es muss sichergestellt werden, dass der Automat bei einem Fehler schnell genug auslöst, bevor die Leitung Schaden nimmt (Brandschutz) und um Personen vor gefährlichen Berührungsspannungen zu schützen (Personenschutz). Die B-Charakteristik ist primär für den Schutz von Leitungskreisen mit ohmschen Verbrauchern wie Beleuchtung und Steckdosen ohne hohe Einschaltströme vorgesehen.',
    details: '**B-Charakteristik (Standard Leitungsschutz):**\n- **Kurzschlussauslösung:** Löst beim 3- bis 5-fachen des Nennstroms aus.\n- **Anwendungsbereiche:** Hauptsächlich für Beleuchtungs- und Steckdosenstromkreise in Wohn- und Zweckbauten, bei denen keine hohen Einschaltströme erwartet werden (z.B. Glühlampen, Heizgeräte, normale Haushaltsgeräte).\n- **Vorteile:** Guter Schutz für Standardleitungen, weit verbreitet.\n- **Nachteile:** Kann bei Geräten mit höheren Einschaltströmen (z.B. manche Motoren, Schaltnetzteile) ungewollt auslösen.\n- **Typische Nennströme:** 6A, 10A, 13A, 16A, 20A, 25A.\n- **Selektivität:** Bei der Planung ist auf Selektivität zu achten, d.h. im Fehlerfall soll nur die dem Fehlerort nächstgelegene Sicherung auslösen.'
  },
  {
    name: 'LS-Automat (C-Charakteristik)',
    titel: 'Leitungsschutzschalter (LS) - C-Charakteristik',
    allgemeineBeschreibung: 'LS-Automaten mit C-Charakteristik sind für Stromkreise mit höheren Einschaltströmen ausgelegt, wie sie bei Motoren oder größeren Lampengruppen auftreten können.',
    auslegungsHinweise: 'Ähnlich wie bei der B-Charakteristik muss der Nennstrom zum Leitungsquerschnitt passen. Die C-Charakteristik wird gewählt, wenn Verbraucher mit induktiven oder kapazitiven Lasten vorhanden sind, die beim Einschalten kurzzeitig hohe Ströme verursachen, die einen B-Automaten auslösen würden.',
    details: '**C-Charakteristik (Geräteschutz mit höheren Einschaltströmen):**\n- **Kurzschlussauslösung:** Löst beim 5- bis 10-fachen des Nennstroms aus.\n- **Anwendungsbereiche:** Für Stromkreise mit Verbrauchern, die höhere Einschaltströme verursachen, z.B. Motoren (Pumpen, Lüfter), Lampengruppen mit elektronischen Vorschaltgeräten (EVGs), größere Schaltnetzteile, Transformatoren.\n- **Vorteile:** Verhindert ungewolltes Auslösen bei betriebsbedingten Einschaltspitzen.\n- **Nachteile:** Bei sehr niederohmigen Kurzschlüssen kann die Abschaltzeit etwas länger sein als bei B-Charakteristik, was bei der Schutzmaßnahmenbewertung zu berücksichtigen ist.\n- **Typische Nennströme:** Ähnlich wie B (6A-63A).'
  },
  {
    name: 'LS-Automat (K-Charakteristik)',
    titel: 'Leitungsschutzschalter (LS) - K-Charakteristik',
    allgemeineBeschreibung: 'LS-Automaten mit K-Charakteristik bieten einen sensiblen Überlastschutz und eine relativ hohe Kurzschlussfestigkeit. Sie werden oft für den Schutz von Motoren und Transformatoren eingesetzt, wo hohe Einschaltströme auftreten, aber auch ein präziser Überlastschutz gefordert ist.',
    auslegungsHinweise: 'Die K-Charakteristik ist besonders für induktive Lasten geeignet. Die Auslegung erfordert genaue Kenntnis der Lastcharakteristik, um Fehlauslösungen zu vermeiden und dennoch einen sicheren Schutz zu gewährleisten.',
    details: '**K-Charakteristik (Kraft, Motoren, Transformatoren):**\n- **Thermische Auslösung (Überlast):** Empfindlich, löst bei ca. 1,05- bis 1,2-fachem Nennstrom relativ schnell aus.\n- **Magnetische Auslösung (Kurzschluss):** Löst beim 8- bis 12-fachen (teilweise bis 14-fachen) des Nennstroms aus.\n- **Anwendungsbereiche:** Motoren, Transformatoren, Stromkreise, die sowohl empfindlichen Überlastschutz als auch Toleranz gegenüber hohen Einschaltströmen benötigen.\n- **Vorteile:** Guter Kompromiss für bestimmte industrielle Anwendungen.\n- **Nachteile:** Weniger verbreitet als B und C, erfordert genauere Planung.\n- **Wichtig:** Immer die Herstellerangaben und spezifischen Anwendungsrichtlinien beachten!'
  },
  {
    name: 'Schmelzsicherung (NEOZED)',
    titel: 'Schmelzsicherung - NEOZED System',
    allgemeineBeschreibung: 'NEOZED-Sicherungen sind Schmelzsicherungen, die Leitungen und Geräte vor Überlast und Kurzschluss schützen. Bei Auslösung muss der Sicherungseinsatz getauscht werden. Sie bieten hohe Kurzschlussausschaltvermögen.',
    auslegungsHinweise: 'Die Auslegung erfolgt nach Nennstrom und Charakteristik (z.B. gG/gL für allgemeinen Leitungsschutz). Wichtig sind der korrekte Sockel, die Passschraube (verhindert den Einsatz falscher Nennströme) und der Sicherungseinsatz. NEOZED wird oft im industriellen Umfeld oder für Vorsicherungen verwendet.',
    details: '**NEOZED-System (D0-System):**\n- **Aufbau:** Besteht aus Sicherungssockel, Passschraube (farbcodiert nach Nennstrom), Sicherungseinsatz und Schraubkappe.\n- **Charakteristik gG/gL:** \'gG\' steht für Ganzbereichs-Kabel- und Leitungsschutz (früher \'gL\'). Diese Sicherungen schützen sowohl vor Überlast als auch vor Kurzschluss.\n- **Vorteile:** Hohes Ausschaltvermögen, robuster Aufbau, eindeutige Farbcodierung der Nennströme.\n- **Nachteile:** Muss nach Auslösung getauscht werden, Auslösezustand nicht immer sofort ersichtlich (Kennmelder beachten).\n- **Größen:** D01 (bis 16A), D02 (bis 63A), D03 (bis 100A - seltener).\n- **Spannungsbereiche:** Typischerweise bis 400V AC / 250V DC.\n- **Anwendung:** Hauptverteilungen, Unterverteilungen, Vorsicherung für Maschinen und Anlagen.'
  },
  {
    name: 'Schmelzsicherung (DIAZED)',
    titel: 'Schmelzsicherung - DIAZED System',
    allgemeineBeschreibung: 'DIAZED-Sicherungen sind eine ältere Bauform von Schmelzsicherungen, ähnlich dem NEOZED-System, aber mit größeren Abmessungen. Sie werden heute seltener neu installiert, sind aber in Bestandsanlagen noch oft zu finden.',
    auslegungsHinweise: 'Die Auslegung und Handhabung ist vergleichbar mit NEOZED. Aufgrund der größeren Bauform und der teilweisen Ablösung durch kompaktere Systeme wie NEOZED oder LS-Schalter, ist bei Neuplanungen oft NEOZED oder LS-Schaltern der Vorzug zu geben. Es gibt auch hier Passschrauben zur Nennstromanpassung.',
    details: '**DIAZED-System (D-System):**\n- **Aufbau:** Ähnlich NEOZED mit Sockel, Passschraube/Passeinsatz, Sicherungseinsatz und Schraubkappe.\n- **Charakteristik gG/gL:** Wie bei NEOZED für allgemeinen Kabel- und Leitungsschutz.\n- **Vorteile:** Robust, hohes Ausschaltvermögen.\n- **Nachteile:** Größere Bauform als NEOZED, muss nach Auslösung getauscht werden.\n- **Größen:** DII (bis 25A), DIII (bis 63A), DIV (bis 100A), DV (bis 200A - sehr selten).\n- **Spannungsbereiche:** Typischerweise bis 500V AC / DC (je nach Ausführung).\n- **Anwendung:** Ältere Haupt- und Unterverteilungen, Industrieanlagen.'
  }
];

const introBlocks = [
  {
    heading: 'Sicherungen: Schutz vor Überlast und Kurzschluss',
    body: 'Sicherungen sind essenzielle Schutzeinrichtungen in elektrischen Anlagen. Ihre Hauptaufgabe ist es, Leitungen und angeschlossene Geräte vor den schädlichen Auswirkungen von Überströmen zu schützen. Überströme können durch Überlast (zu viele Verbraucher oder ein Verbraucher mit zu hoher Leistung) oder durch einen Kurzschluss (direkte, niederohmige Verbindung zwischen spannungsführenden Teilen) entstehen. Ohne Sicherungen könnten Leitungen überhitzen, Isolierungen schmelzen und Brände verursachen oder Geräte zerstört werden.'
  },
  {
    heading: 'Grundlagen der Auslegung von Sicherungen',
    body: 'Die korrekte Auslegung einer Sicherung ist entscheidend für die Sicherheit und Funktion einer elektrischen Anlage. Dabei müssen mehrere Faktoren berücksichtigt werden:\n\n- **Leitungsquerschnitt:** Die Sicherung muss so gewählt werden, dass sie auslöst, bevor die Leitung durch den Überstrom unzulässig erwärmt wird (Schutz vor Überlast).\n- **Art der Last:** Verbraucher mit hohen Einschaltströmen (z.B. Motoren) benötigen Sicherungen mit einer trägeren Auslösecharakteristik als ohmsche Verbraucher (z.B. Heizungen).\n- **Kurzschlussstrom:** Die Sicherung muss den maximal auftretenden Kurzschlussstrom sicher abschalten können (Ausschaltvermögen).\n- **Selektivität:** Im Fehlerfall soll nur die Sicherung auslösen, die dem Fehlerort am nächsten ist, um nicht unnötig große Teile der Anlage abzuschalten.\n- **Umgebungsbedingungen:** Temperatur und Verlegeart der Leitungen können die Strombelastbarkeit beeinflussen.\n\nDie genaue Auslegung erfolgt nach den geltenden Normen und Vorschriften (z.B. VDE-Bestimmungen in Deutschland).'
  }
];

const M6_U7_X1_Sicherung: React.FC = () => {
  const navigate = useNavigate();
  const { token, canEditTextsM6, canUploadImagesM6 } = useAuth();
  const canEditTexts = canEditTextsM6;
  const canUploadImages = canUploadImagesM6;

  const { override, refresh } = useUnitOverride(PAGE_ID);

  const getImageSrc = (key: string, fallback: string) => {
    const v = override?.images?.[key];
    if (!v) return fallback;
    let s = String(v).trim();
    if (s.includes('/')) {
      s = s.split('/').pop() || s;
    }
    return `/assets/unit_custom/${s}`;
  };

  const getDynamicImageSrc = (selectedType: string, idx: number, fallback: string) => {
    const key = `dyn_${selectedType}_img_${idx}`;
    return getImageSrc(key, fallback);
  };

  const [selectedType, setSelectedType] = React.useState<string>(sicherungstypen[0].name);
  const currentInfo = sicherungstypen.find(s => s.name === selectedType) || sicherungstypen[0];

  const mergedIntroBlocks = React.useMemo(
    () => introBlocks.map((tb, i) => ({ ...tb, body: override?.texts?.[`intro_${i}`] || tb.body })),
    [override]
  );

  const mergedDynamicBlocks = React.useMemo(() => [
    { heading: currentInfo.titel, body: override?.texts?.[`${selectedType}_0`] || currentInfo.allgemeineBeschreibung },
    { heading: 'Hinweise zur Auslegung', body: override?.texts?.[`${selectedType}_1`] || currentInfo.auslegungsHinweise },
    { heading: 'Detaillierte Informationen', body: override?.texts?.[`${selectedType}_2`] || currentInfo.details }
  ], [selectedType, override, currentInfo]);

  const allTextBlocks = React.useMemo(
    () => [...mergedIntroBlocks, ...mergedDynamicBlocks],
    [mergedIntroBlocks, mergedDynamicBlocks]
  );

  const fullPlainText = React.useMemo(() => 
    allTextBlocks.map(b => `${b.heading}. ${b.body}`).join(' '),
    [allTextBlocks]
  );

  const { speak, stop, speaking } = useTTS({ text: fullPlainText });

  const handleImageReplace = React.useCallback(async (imageKey: string, file: File) => {
    if (!token) return alert('Kein Token – bitte einloggen.');
    try {
      await uploadUnitAsset(PAGE_ID, imageKey, file, token);
      await refresh();
    } catch (error) {
      console.error('Fehler beim Bild-Upload:', error);
      alert('Fehler beim Hochladen des Bildes.');
    }
  }, [token, refresh]);

  const handleTextSave = React.useCallback(async (blockId: string, newBody: string) => {
    if (!token) return alert('Kein Token – bitte einloggen.');
    try {
      await updateUnitContent(PAGE_ID, { texts: { [blockId]: newBody } }, token);
      await refresh();
    } catch (error) {
      console.error('Fehler beim Text-Speichern:', error);
      alert('Fehler beim Speichern des Textes.');
    }
  }, [token, refresh]);

  const handleTypeChange = (event: any) => {
    setSelectedType(String(event.target.value));
  };

  const ttsButton = React.createElement(IconButton, {
    onClick: speaking ? stop : speak,
    'aria-label': 'Text vorlesen',
    sx: { ml: 1 }
  }, React.createElement(SpeakerIcon, { sx: { fontSize: 28 } }));

  return React.createElement(UnitPageShell, { title: '6.7.1 Sicherungen im Schaltschrank', actions: ttsButton },
    React.createElement(Box, { sx: { display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 } },
      React.createElement(Box, { sx: { display: 'flex', flexDirection: 'column', gap: 2 } },
        IMAGE_KEYS.map((key) =>
          React.createElement(ZoomableImage, {
            key: key,
            src: getImageSrc(key, `${IMAGE_BASE}zzz_help.png`),
            alt: 'Sicherung',
            label: 'Sicherungsbild',
            onReplace: canUploadImages ? (file: File) => handleImageReplace(key, file) : undefined
          })
        )
      ),
      React.createElement(Box, { sx: { display: 'flex', flexDirection: 'column', gap: 2 } },
        React.createElement(EditableTextBlock, {
          key: 0,
          pageId: PAGE_ID,
          blockId: 'intro_0',
          heading: mergedIntroBlocks[0].heading,
          body: mergedIntroBlocks[0].body,
          onSave: (newBody: string) => handleTextSave('intro_0', newBody)
        }),
        React.createElement(EditableTextBlock, {
          key: 1,
          pageId: PAGE_ID,
          blockId: 'intro_1',
          heading: mergedIntroBlocks[1].heading,
          body: mergedIntroBlocks[1].body,
          onSave: (newBody: string) => handleTextSave('intro_1', newBody)
        }),
        React.createElement(FormControl, { fullWidth: true, sx: { mt: 2, mb: 2 } },
          React.createElement(InputLabel, { id: 'sicherung-select-label' }, 'Sicherungstyp auswählen'),
          React.createElement(Select, {
            labelId: 'sicherung-select-label',
            id: 'sicherung-select',
            value: selectedType,
            label: 'Sicherungstyp auswählen',
            onChange: handleTypeChange
          },
            ...sicherungstypen.map((typ) =>
              React.createElement(MenuItem, { key: typ.name, value: typ.name }, typ.name)
            )
          )
        ),
        React.createElement(Box, { sx: { display: 'flex', flexDirection: 'column', gap: 2 } },
          IMAGE_KEYS_DYNAMIC.map((key) =>
            React.createElement(ZoomableImage, {
              key: key,
              src: getDynamicImageSrc(selectedType, 0, `${IMAGE_BASE}zzz_help.png`),
              alt: 'Bild für Spinner-Typ',
              label: 'Spinner-Bild',
              onReplace: canUploadImages ? (file: File) => handleImageReplace(`dyn_${selectedType}_img_0`, file) : undefined
            })
          )
        ),
        React.createElement(EditableTextBlock, {
          key: `${selectedType}_0`,
          pageId: PAGE_ID,
          blockId: `dyn_${selectedType}_0`,
          heading: currentInfo.titel,
          body: override?.texts?.[`dyn_${selectedType}_0`] || currentInfo.allgemeineBeschreibung,
          onSave: (newBody: string) => handleTextSave(`dyn_${selectedType}_0`, newBody)
        }),
        React.createElement(EditableTextBlock, {
          key: `${selectedType}_1`,
          pageId: PAGE_ID,
          blockId: `dyn_${selectedType}_1`,
          heading: 'Hinweise zur Auslegung',
          body: override?.texts?.[`dyn_${selectedType}_1`] || currentInfo.auslegungsHinweise,
          onSave: (newBody: string) => handleTextSave(`dyn_${selectedType}_1`, newBody)
        }),
        React.createElement(EditableTextBlock, {
          key: `${selectedType}_2`,
          pageId: PAGE_ID,
          blockId: `dyn_${selectedType}_2`,
          heading: 'Detaillierte Informationen',
          body: override?.texts?.[`dyn_${selectedType}_2`] || currentInfo.details,
          onSave: (newBody: string) => handleTextSave(`dyn_${selectedType}_2`, newBody)
        }),
        React.createElement(Box, { sx: { display: 'flex', gap: 1, mt: 2 } },
          React.createElement(Button, {
            variant: 'outlined',
            onClick: () => navigate('/units/m6_u7_x99_quiz')
          }, 'Zum Quiz')
        )
      )
    )
  );
};

export default M6_U7_X1_Sicherung;


